import AuthPageFrame from '../AuthPageFrame';
import LoadingIndicator from '@/components/ui/LoadingIndicator';
import { Button } from '@/components/ui/button';
import { Check, Mail, MailCheck } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { VerifyEmailAction, VerifyEmailResultProps, VerifyEmailState, VerifyEmailStateContent } from './types';

const verifyEmailStateContent: Record<VerifyEmailState, VerifyEmailStateContent> = {
  already_confirmed: {
    actions: ['goToApp'],
    body: 'Этот адрес подтверждён раньше. Ничего делать не нужно.',
    title: 'Почта уже подтверждена',
    tone: 'success'
  },
  confirmed: {
    actions: ['goToApp'],
    body: 'Адрес подтверждён. Можно продолжать работу в приложении.',
    title: 'Почта подтверждена',
    tone: 'success'
  },
  expired: {
    actions: ['resend', 'goToApp'],
    body: 'Ссылка из письма действует 24 часа, и этот срок истёк. Отправим новое письмо.',
    title: 'Ссылка больше не действует',
    tone: 'error'
  },
  not_found: {
    actions: ['goToApp'],
    body: 'Возможно, она скопирована не полностью. Войдите в приложение и запросите новое письмо в профиле.',
    title: 'Ссылка не сработала',
    tone: 'error'
  },
  resent: {
    actions: ['resend', 'goToApp'],
    body: 'Откройте письмо и перейдите по ссылке — она действует 24 часа.',
    title: 'Письмо отправлено',
    tone: 'neutral'
  }
};

const actionText: Record<VerifyEmailAction, string> = {
  goToApp: 'Перейти в приложение',
  resend: 'Отправить повторно'
};

const getStateBody = (emailMasked: string | null, state: VerifyEmailState): string => {
  if (!emailMasked) return verifyEmailStateContent[state].body;

  if (state === 'confirmed') {
    return `Адрес ${emailMasked} подтверждён. Можно продолжать работу в приложении.`;
  }

  if (state === 'expired') {
    return `Ссылка из письма действует 24 часа, и этот срок истёк. Отправим новое письмо на ${emailMasked}.`;
  }

  if (state === 'resent') {
    return `Письмо отправлено на ${emailMasked}. Откройте его и перейдите по ссылке — она действует 24 часа.`;
  }

  return verifyEmailStateContent[state].body;
};

const getActionContent = (action: VerifyEmailAction, resendSecondsLeft: number) => {
  if (action === 'resend' && resendSecondsLeft) {
    return (
      <>
        {'Отправить повторно через '}
        <span className="inline-block w-[2ch] text-center tabular-nums">{resendSecondsLeft}</span>
        {' сек'}
      </>
    );
  }

  return actionText[action];
};

const getIsActionDisabled = (
  action: VerifyEmailAction,
  isGoToAppDisabled: boolean,
  isResending: boolean,
  resendSecondsLeft: number
): boolean => {
  if (action === 'goToApp') return isGoToAppDisabled;

  return Boolean(resendSecondsLeft) || isResending;
};

const VerifyEmailResult = (props: VerifyEmailResultProps) => {
  const {
    emailMasked = null,
    isGoToAppDisabled = false,
    isLoading = false,
    isResending = false,
    onGoToApp,
    onResend,
    resendSecondsLeft = 0,
    state
  } = props;

  const content = verifyEmailStateContent[state];
  const Icon = state === 'resent' ? Mail : content.tone === 'success' ? Check : MailCheck;
  const actions = content.actions;

  if (isLoading) {
    return (
      <AuthPageFrame subtitle="Подтверждение почты">
        <div className="flex min-h-[204px] items-center justify-center">
          <LoadingIndicator
            label="Проверяем почту"
            size="xl"
          />
        </div>
      </AuthPageFrame>
    );
  }

  return (
    <AuthPageFrame subtitle="Подтверждение почты">
      <div
        className={cn(
          'flex flex-col items-center text-center',
          state === 'resent' ? 'gap-4' : 'gap-6'
        )}
      >
        <div
          className={cn(
            'flex size-16 items-center justify-center',
            'rounded-full',
            content.tone === 'error' && 'bg-[hsl(var(--error-bg))] text-[hsl(var(--error-text))]',
            content.tone === 'neutral' && 'bg-state-disabled-bg/5 text-foreground',
            content.tone === 'success' && 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success-text))]'
          )}
        >
          {content.tone === 'error' ? (
            <span
              className="font-sans text-[32px] font-semibold leading-none"
              aria-hidden
            >
              !
            </span>
          ) : (
            <Icon
              className={state === 'resent' ? 'size-6' : 'size-7'}
              aria-hidden
              strokeWidth={1.75}
            />
          )}
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <h1 className="font-sans text-2xl font-semibold normal-case leading-8 tracking-normal text-foreground">
            {content.title}
          </h1>
          <p className="text-sm leading-5 text-muted-foreground">{getStateBody(emailMasked, state)}</p>
        </div>

        <div
          className={cn(
            'flex w-full flex-col',
            state === 'resent' ? 'gap-4' : 'gap-3'
          )}
        >
          {actions.map((action) => {
            const isActionDisabled = getIsActionDisabled(
              action,
              isGoToAppDisabled,
              isResending,
              resendSecondsLeft
            );

            return (
              <Button
                className={cn(
                  'w-full',
                  action === 'resend' && resendSecondsLeft && 'disabled:text-muted-foreground'
                )}
                disabled={isActionDisabled}
                key={action}
                loading={action === 'resend' && isResending}
                onClick={action === 'goToApp' ? onGoToApp : onResend}
                size="lg"
                type="button"
                variant={action === 'goToApp' && actions.length > 1 ? 'tertiary' : 'primary'}
              >
                {getActionContent(action, resendSecondsLeft)}
              </Button>
            );
          })}
        </div>
      </div>
    </AuthPageFrame>
  );
};

export default VerifyEmailResult;